import type { OpportunityRecord } from "../api/client";
const FIELDS: Record<string, string> = {
  organization_name: "naziv organizacije", organization_type: "vrsta organizacije",
  region: "regija", property_count_band: "broj objekata", website: "web-stranica",
  integrations_in_use: "sustavi u upotrebi", observed_sales_signals: "uočene poslovne potrebe",
  organization_group: "skupina organizacije",
};
const VALUES: Record<string, string> = {
  hotel: "Hotel", villa: "Vile", apartment: "Apartmani", agency: "Agencija", lodging: "Smještaj",
  "1": "1 objekt", "2-5": "2–5 objekata", "6-20": "6–20 objekata", "21-50": "21–50 objekata", "51+": "51 ili više objekata",
  pms: "sustav za upravljanje objektima (PMS)", channel_manager: "upravitelj prodajnih kanala",
  booking_engine: "sustav za izravne rezervacije", crm: "sustav za odnose s klijentima (CRM)",
  payment_gateway: "sustav za naplatu", revenue_management: "sustav za upravljanje prihodima",
  none: "nema zabilježenih integracija",
  direct_booking_share_rising: "Raste udio izravnih rezervacija", manual_channel_updates: "Prodajni kanali ažuriraju se ručno",
  multi_property_expansion: "Širi se portfelj objekata", no_online_booking: "Nema online rezervacija",
  payment_reconciliation_manual: "Uplate se usklađuju ručno", rate_parity_issues: "Cijene se razlikuju među prodajnim kanalima",
  "synthetic-city": "Testna regija: grad", "synthetic-coast-north": "Testna regija: sjeverna obala",
  "synthetic-coast-south": "Testna regija: južna obala", "synthetic-islands": "Testna regija: otoci", "synthetic-mountain": "Testna regija: planine",
};
export function opportunityFieldLabel(field: string): string { return FIELDS[field] ?? "Dodatni podatak"; }
export function opportunityValueLabel(value: string): string { return VALUES[value] ?? value; }
/** Facts in these fields are blocked by missing, uncited, stale or contradictory evidence. */
export function opportunityBlockedFields(record: OpportunityRecord): Set<string> {
  return new Set([...record.missing_fields, ...record.uncited_fields,
    ...record.contradictions.map((item) => item.field),
    ...record.reason_codes.filter((code) => code.startsWith("stale_")).map((code) => code.slice(6))]);
}
/** A display summary of cited facts, never a new inference or evidence claim. */
export function opportunityDescription(record: OpportunityRecord): string {
  if (record.outcome === "rejected") return record.suppression.suppressed
    ? "Zapis je isključen iz razmatranja prema pravilima isključenja. Nije preporučen daljnji rad."
    : "Zapis nije prošao provjeru podataka. Pouzdan opis nije dostupan; potrebno je provjeriti izvore.";
  const blocked = opportunityBlockedFields(record);
  const values = (field: string) => blocked.has(field) ? [] : record.verified_facts
    .filter((fact) => fact.field === field).map((fact) => opportunityValueLabel(fact.value));
  const parts: string[] = [];
  const type = values("organization_type")[0], band = values("property_count_band")[0];
  // Bands count OBJECTS in the portfolio, never rooms or guests. Naming the
  // dimension explicitly prevents a plausible but unsupported commercial reading.
  if (type) parts.push(`Vrsta organizacije: ${type}.`);
  if (band) parts.push(`Portfelj: ${band}.`);
  const integrations = values("integrations_in_use");
  if (integrations.length) {
    parts.push(
      integrations.every((value) => value === VALUES.none)
        ? "Prema izvorima nema zabilježenih integracija."
        : `U upotrebi: ${integrations.join(", ")}.`,
    );
  }
  const signals = values("observed_sales_signals");
  if (signals.length) parts.push(`${signals.join("; ")}.`);
  if (!parts.length) parts.push("Nema dovoljno potvrđenih podataka za pouzdan opis organizacije.");
  if (record.contradictions.length) parts.push(`Izvori se ne slažu: ${record.contradictions.map((x) => opportunityFieldLabel(x.field)).join(", ")}.`);
  const missing = Array.from(new Set([...record.missing_fields, ...record.uncited_fields]));
  if (missing.length) parts.push(`Nije potvrđeno: ${missing.map(opportunityFieldLabel).join(", ")}.`);
  if (record.reason_codes.includes("stale_evidence")) parts.push("Dio izvora je zastario; provjeriti aktualnost podataka.");
  if (record.outcome === "duplicate") parts.push("Ovo je duplikat već evidentirane organizacije.");
  return parts.join(" ");
}
